import React, { Suspense, lazy, useState, useRef, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/useAuth';
import { Role, hasRole } from './lib/roles';
import { NIDATA_LOADING_LABEL } from './lib/nidataUiCopy';
import { AuthenticatedLayout } from './components/AuthenticatedLayout';
import { NavShellFiltersProvider } from './contexts/NavShellFiltersContext';
import { IncidentDashboardPdfExportProvider } from './contexts/IncidentDashboardPdfExportContext';
import { StickyBackButton } from './components/StickyBackButton';
import {
  isLeaderAssessmentEnabled,
  LEADER_ASSESSMENT_PATH,
  LEADER_ASSESSMENT_PATH_SEGMENT,
} from './features/leader-assessment/config';
import { safeLazyImport } from './utils/safeLazyImport';
import { isNsoPublicPath } from './features/nso-public/access';

const LoginPage = lazy(() => import('./components/Login'));
const HomePage = lazy(() => import('./pages/HomePage'));
const ComplianceHub = lazy(safeLazyImport(() => import('./components/ComplianceHub')));
const UserAdminPage = lazy(() => import('./pages/UserAdminPage'));
const RoleAdminPage = lazy(() => import('./pages/RoleAdminPage'));
const DeptAdminPage = lazy(() => import('./pages/DeptAdminPage'));
const ComplianceAdminPage = lazy(() => import('./pages/ComplianceAdminPage'));
const AdminToolsPage = lazy(() => import('./pages/AdminToolsPage'));
const AuditLogViewer = lazy(() => import('./components/AuditLogViewer'));
const TeamResultsPage = lazy(() => import('./pages/TeamResultsPage'));
const TGProjectPage = lazy(() => import('./pages/TGProjectPage'));
const TeamEthicPage = lazy(() => import('./pages/TeamEthicPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const KPIsNursingPage = lazy(() => import('./pages/KPIsNursingPage'));
const MedErrorIncidentPage = lazy(() => import('./pages/MedErrorIncidentPage'));
const FallIncidentPage = lazy(() => import('./pages/FallIncidentPage'));
const QualityReportsPage = lazy(() => import('./pages/QualityReportsPage'));
const KpoaIncidentPage = lazy(() => import('./pages/KpoaIncidentPage'));
const HrImportPage = lazy(() => import('./pages/HrImportPage'));
const SafetyCulture2569Page = lazy(() => import('./pages/SafetyCulture2569Page'));
const PublicSiteLayout = lazy(() =>
  import('./components/nso-public/PublicSiteLayout').then((m) => ({ default: m.PublicSiteLayout })),
);
const NsoPublicPage = lazy(() => import('./pages/NsoPublicPage'));
const NsoWebStandardPage = lazy(() =>
  import('./components/nso-public/NsoWebStandardPage').then((m) => ({ default: m.NsoWebStandardPage })),
);
const NsoPublicAccessGate = lazy(() =>
  import('./components/nso-public/NsoPublicAccessGate').then((m) => ({ default: m.NsoPublicAccessGate })),
);

const leaderAssessmentEnabled = isLeaderAssessmentEnabled();

const PUBLIC_ROUTE_PREFIXES = ['/kpis-nursing', '/hr', '/login'] as const;

function isPublicRoutePath(pathname: string): boolean {
  return PUBLIC_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function LoadingScreen(): React.ReactElement {
  return (
    <div className="flex h-screen items-center justify-center" aria-busy="true" aria-label={NIDATA_LOADING_LABEL}>
      {NIDATA_LOADING_LABEL}
    </div>
  );
}

const AuthenticatedRoute: React.FC<{ children: React.ReactElement; roles?: Role[] }> = ({ children, roles }) => {
  const { authUser, userRole, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!authUser) {
    if (location.pathname === '/') {
      return <Navigate to="/nso" replace />;
    }
    const isLeaderEntry =
      leaderAssessmentEnabled &&
      (location.pathname === LEADER_ASSESSMENT_PATH ||
        location.pathname === `${LEADER_ASSESSMENT_PATH}/`);
    const loginTarget = isLeaderEntry
      ? `/login?returnTo=${encodeURIComponent(LEADER_ASSESSMENT_PATH)}`
      : '/login';
    return <Navigate to={loginTarget} replace />;
  }

  if (roles && (!userRole || !hasRole(userRole, roles))) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const REPORT_PROBLEM_URL = 'https://forms.gle/aBs58indgkFeAtJV6';
const FEEDBACK_URL = 'https://forms.gle/R55VF4NamUSLfSSp6';

class RouteErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; errorMessage: string; componentStack: string }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, errorMessage: '', componentStack: '' };
  }

  static getDerivedStateFromError(error: unknown) {
    let msg = 'An unexpected error occurred';
    try {
      if (error instanceof Error && typeof error.message === 'string') {
        msg = error.message;
      }
    } catch {
      /* error.message access can throw on exotic errors */
    }
    return { hasError: true, errorMessage: msg };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? '' });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 p-8">
          <h1 className="text-lg font-semibold text-slate-800">เกิดข้อผิดพลาด</h1>
          <p className="max-w-md text-center text-sm text-slate-600">{this.state.errorMessage}</p>
          {import.meta.env.DEV && this.state.componentStack ? (
            <pre className="max-h-52 w-full max-w-3xl overflow-auto rounded border border-slate-200 bg-white p-3 text-left text-xs text-slate-700">
              {this.state.componentStack}
            </pre>
          ) : null}
          <button
            type="button"
            onClick={() => this.setState({ hasError: false, errorMessage: '', componentStack: '' })}
            className="rounded-lg bg-slate-200 px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-300"
          >
            ลองอีกครั้ง
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const ReportProblemButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="fixed bottom-4 right-4 z-50">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-9 h-9 flex items-center justify-center bg-slate-700 text-white text-base font-medium rounded-lg shadow-md hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
        aria-label="Help"
        aria-expanded={open}
      >
        ?
      </button>
      {open && (
        <div className="absolute bottom-full right-0 mb-2 py-1 bg-white rounded-lg shadow-lg border border-slate-200 min-w-[160px]">
          <a
            href={REPORT_PROBLEM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-t-lg"
            onClick={() => setOpen(false)}
          >
            แจ้งปัญหา
          </a>
          <a
            href={FEEDBACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-b-lg"
            onClick={() => setOpen(false)}
          >
            ประเมิน/ติชม
          </a>
        </div>
      )}
    </div>
  );
};

const AppRoutes: React.FC = () => {
  const leaderAssessmentDisabledRedirect = <Navigate to="/" replace />;

  return (
    <RouteErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/kpis-nursing" element={<KPIsNursingPage />} />
          <Route path="/hr" element={<HrImportPage />} />
          {/* Outer NSO site — public. Guests who open / are sent here. */}
          <Route path="/nso" element={<NsoPublicAccessGate />}>
            <Route element={<PublicSiteLayout />}>
              <Route index element={<NsoPublicPage />} />
              <Route path="web-development-standard" element={<NsoWebStandardPage />} />
              <Route path="*" element={<NsoPublicPage />} />
            </Route>
          </Route>
          <Route path={LEADER_ASSESSMENT_PATH_SEGMENT} element={leaderAssessmentDisabledRedirect} />
          {/* NiData intra — always login-required */}
          <Route
            path="/"
            element={
              <AuthenticatedRoute>
                <IncidentDashboardPdfExportProvider>
                  <NavShellFiltersProvider>
                    <AuthenticatedLayout />
                  </NavShellFiltersProvider>
                </IncidentDashboardPdfExportProvider>
              </AuthenticatedRoute>
            }
          >
            <Route index element={<HomePage />} />
            <Route path="report" element={<ComplianceHub />} />
            <Route path="quality-reports" element={<QualityReportsPage />} />
            <Route path="kpis" element={<KPIsNursingPage />} />
            <Route path="team" element={<TeamResultsPage />} />
            <Route path="team/occ" element={<TGProjectPage />} />
            <Route path="team/occ/report" element={<Navigate to="/team/occ" replace />} />
            <Route path="team/ethic" element={<TeamEthicPage />} />
            <Route path="team/occ/report/:participantId" element={<Navigate to="/team/occ" replace />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="users" element={<AuthenticatedRoute roles={['admin']}><UserAdminPage /></AuthenticatedRoute>} />
            <Route path="roles" element={<AuthenticatedRoute roles={['admin']}><RoleAdminPage /></AuthenticatedRoute>} />
            <Route path="departments" element={<AuthenticatedRoute roles={['admin']}><DeptAdminPage /></AuthenticatedRoute>} />
            <Route path="compliance-categories" element={<AuthenticatedRoute roles={['admin']}><ComplianceAdminPage /></AuthenticatedRoute>} />
            <Route path="admin/migration" element={<AuthenticatedRoute roles={['admin']}><AdminToolsPage /></AuthenticatedRoute>} />
            <Route path="med-error-incidents" element={<MedErrorIncidentPage />} />
            <Route path="fall-incidents" element={<FallIncidentPage />} />
            <Route path="incident-export" element={<Navigate to="/report?tab=export" replace />} />
            <Route path="kpoa-incidents" element={<KpoaIncidentPage />} />
            <Route path="safety-culture-2569" element={<SafetyCulture2569Page />} />
            <Route path="audit-logs" element={<AuthenticatedRoute roles={['admin']}><AuditLogViewer /></AuthenticatedRoute>} />
            <Route path="leader-assessment/dashboard" element={leaderAssessmentDisabledRedirect} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  );
};

const App: React.FC = () => {
  const location = useLocation();
  const { loading } = useAuth();
  const onPublicRoute = isPublicRoutePath(location.pathname) || isNsoPublicPath(location.pathname);
  const hideStickyBack = onPublicRoute || isNsoPublicPath(location.pathname);

  return (
    <>
      {loading && !onPublicRoute ? <LoadingScreen /> : <AppRoutes />}
      {!hideStickyBack ? <StickyBackButton /> : null}
      <ReportProblemButton />
    </>
  );
};

export default App;
